import { useRef } from 'react';
import { useAPIRoute } from '../../hooks';
import { useQuery } from 'react-query';
import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models/neighborhood';
import { useV2APIRoute } from '../../hooks/useAPIRoute';
import { useGlobal } from '../../hooks/useGlobal';
import { fetchAllV2ServiceAreas } from '../v2/fetchV2ServiceAreaList';

type useServiceAreasListProps = {
    accountId: string;
};

export const useServiceAreasList = ({
    accountId,
}: useServiceAreasListProps) => {
    const url = useAPIRoute('/serviceareas/list', 'v1');
    const v2Url = useV2APIRoute('neighborhood');
    const { v2ServiceAreaData } = useGlobal();
    // The prefetch is a snapshot the plugin captured once at bootstrap. Feeding
    // it in on every query run would pin page 1 to that snapshot for the life of
    // the widget while pages 2+ came back fresh — so a refetch would return a
    // stale first page stitched to current later ones, and never actually
    // refresh page 1. Consume it once; refetches go to the service for
    // everything.
    const prefetchConsumed = useRef(false);

    return useQuery<NeighborhoodServiceAreaInfo[]>(
        // v2 is part of the key: flipping the flag must not serve a cached
        // response produced by the other backend.
        ['serviceAreasList', accountId, v2Url ? 'v2' : 'v1'],
        async () => {
            if (v2Url) {
                const prefetched = prefetchConsumed.current
                    ? undefined
                    : v2ServiceAreaData;
                prefetchConsumed.current = true;

                const areas = await fetchAllV2ServiceAreas({
                    url: v2Url,
                    accountId,
                    prefetched,
                });
                // undefined means a page failed and was reported; fall through to
                // v1 rather than render a partial map as if it were complete.
                if (areas) {
                    return areas;
                }
            }

            try {
                const result = await fetch(
                    `${url}?` +
                        new URLSearchParams({
                            accountId: accountId,
                        }),
                    {
                        method: 'GET',
                    }
                );
                return result.json();
            } catch (e) {
                console.error('useServiceAreasList error:', e);
                throw e;
            }
        }
    );
};
